<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultEditing, useDefaultGridOptions, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import { useAvatarColumn } from '@/composables/data-grid-column'
import { useDetectDevice } from '@/composables/device'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

import ContactEdit from '@/components/shared/ContactEdit.vue'

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const { isMobile } = useDetectDevice()

const { gridRef } = useGridRef()
const editItem = ref(null)

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.contactsUrl, {
  customParamsFn: () => ({ company_id: props.company.id }),
})

const onRowRemoving = (e) => {
  e.cancel = planningStore.deleteContactAsync(e.data.id)
}

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { name: 'edit', onClick: (e) => { editItem.value = { ...e.row.data } } },
      { name: 'delete' },
    ],
    fixed: false,
    visibleIndex: -1,
    width: '6rem',
  },
  useAvatarColumn(),
  { dataField: 'name', caption: i18n.t('common.name'), sortOrder: 'asc' },
  { dataField: 'surname', caption: i18n.t('common.lastName') },
  { dataField: 'role', caption: i18n.t('common.role') },
  { dataField: 'default_email', caption: i18n.t('common.email'), allowEditing: false, allowSorting: false, allowFiltering: false },
  { dataField: 'default_phone', caption: i18n.t('common.phone'), allowEditing: false, allowSorting: false, allowFiltering: false },
  { dataField: 'is_default', caption: i18n.t('company.defaultContact'), dataType: 'boolean', width: '8rem' },
]

const gridOptions = useDefaultGridOptions({
  id: 'company-contacts-grid-v3',
  columns: columns,
  dataSource: gridDataSource,
  editing: useDefaultEditing(null, null, { allowAdding: false, allowUpdating: true }),
  onRowRemoving: onRowRemoving,
  addButton: {
    onClick: () => { editItem.value = { company: props.company.id, references: [], is_default: false } },
  },
})

const updateGrid = async(saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}
</script>

<template>
  <div>
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      class="dialog-md"
      @hide="updateGrid"
    >
      <contact-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    />
  </div>
</template>
